import { useEffect, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { buildZip } from "../../lib/socialExport";
import {
  VARIANTS,
  loadAssets,
  buildSvg,
  rasterSquare,
  variantFiles,
  LOGO_README,
} from "../../lib/logoPack";

const svgUrl = (svg) => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

const Tile = ({ label, children, testid }) => (
  <div className="flex flex-col items-center gap-1.5" data-testid={testid}>
    <div className="flex h-28 items-center justify-center">{children}</div>
    <span className="text-[11px] font-extrabold uppercase tracking-wide text-[#6e655b]">{label}</span>
  </div>
);

const usePreviews = (dark) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let alive = true;
    setRows(null);
    (async () => {
      await loadAssets();
      const out = [];
      for (const v of Object.keys(VARIANTS)) {
        const square = buildSvg({ variant: v, dark, stacked: true });
        out.push({
          v,
          fav16: await rasterSquare(square, 16, { pad: v === "logo" ? 0.03 : 0.07, dark }),
          fav48: await rasterSquare(square, 48, { pad: v === "logo" ? 0.03 : 0.07, dark }),
          profile: await rasterSquare(square, 400, { pad: 0.16, dark }),
          vector: v === "logo" ? square.svg : buildSvg({ variant: v, dark, stacked: false }).svg,
        });
      }
      if (alive) setRows(out);
    })().catch(() => toast.error("Could not load the logo artwork"));
    return () => {
      alive = false;
    };
  }, [dark]);
  return rows;
};

export const LogoPackPanel = ({ dark, busy, setBusy }) => {
  const rows = usePreviews(dark);
  const bg = dark ? "#2d2824" : "#fdfbf7";

  const exportPack = async () => {
    setBusy("logo");
    try {
      await loadAssets();
      let files = {};
      for (const d of [true, false]) {
        for (const v of Object.keys(VARIANTS)) files = { ...files, ...(await variantFiles(v, d)) };
      }
      await buildZip("foinn-org-logo-pack", [], { ...files, "README.txt": LOGO_README });
      toast.success(`${Object.keys(files).length} logo files exported`);
    } catch {
      toast.error("Export failed");
    } finally {
      setBusy("");
    }
  };

  return (
    <>
      <p className="text-sm font-semibold text-[#6e655b]">
        Three versions — logo · logo + Foinn.org · logo + Foinn.org + Folk Tune Finder — each as favicon
        (16 → 512 px + .ico), profile picture (400 / 800 px, round-avatar safe) and vector SVG. The zip holds
        both dark and light.
      </p>
      <p className="mt-2 text-xs font-bold text-[#8a2be2]" data-testid="logo-favicon-tip">
        Tip: at 16–48 px the lettering can't be read — use version a (logo only) as the site favicon.
      </p>
      <div className="mt-4">
        <button
          onClick={exportPack}
          disabled={!!busy}
          className="flex items-center gap-2 rounded-full bg-[#8a2be2] px-5 py-2.5 text-sm font-extrabold text-white transition-transform hover:scale-105 disabled:opacity-50"
          data-testid="export-logo"
        >
          {busy === "logo" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Export logo pack
        </button>
      </div>

      <div className="mt-6 space-y-4" data-testid="logo-preview">
        {!rows && <Loader2 className="h-6 w-6 animate-spin text-[#6e655b]" />}
        {rows?.map((r) => (
          <div key={r.v} className="rounded-2xl border border-[#e5e0d8] p-4" data-testid={`logo-row-${r.v}`}>
            <h3 className="text-sm font-extrabold text-[#2d2824]">{VARIANTS[r.v].label}</h3>
            <div className="mt-3 flex flex-wrap items-end gap-6">
              <Tile label="Favicon 16" testid={`logo-fav16-${r.v}`}>
                <img src={r.fav16} alt="" style={{ width: 32, height: 32, imageRendering: "pixelated" }} />
              </Tile>
              <Tile label="Favicon 48" testid={`logo-fav48-${r.v}`}>
                <img src={r.fav48} alt="" style={{ width: 48, height: 48 }} />
              </Tile>
              <Tile label="Profile" testid={`logo-profile-${r.v}`}>
                <img src={r.profile} alt="" className="h-28 w-28 rounded-full border border-[#e5e0d8]" />
              </Tile>
              <Tile label="Vector SVG" testid={`logo-vector-${r.v}`}>
                <div className="flex h-28 items-center rounded-xl px-4" style={{ background: bg }}>
                  <img src={svgUrl(r.vector)} alt="" className="max-h-20 max-w-[260px]" />
                </div>
              </Tile>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};
